// Website header. `overlay` floats over a full-bleed hero and turns solid on scroll.
import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { PiCalendarCheck, PiList, PiSignOut, PiUserCircle, PiX } from 'react-icons/pi';
import AuthModal from '../components/Auth/AuthModal';
import { notifySessionChange, signOut, useSession } from './session';
import { Button } from './ui';

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/book', label: 'Book a ride' },
  { to: '/fleet', label: 'Fleet' },
  { to: '/about', label: 'About' },
  { to: '/help', label: 'Help' },
  { to: '/contact', label: 'Contact' },
];

// The website stores camelCase names (auth responses); accept snake_case too.
const firstName = (user) => user.firstName || user.first_name || '';
const lastName = (user) => user.lastName || user.last_name || '';
const initials = (user) =>
  [firstName(user), lastName(user)].filter(Boolean).map((n) => n[0]).join('').toUpperCase() ||
  (user.email || '?')[0].toUpperCase();

export default function SiteHeader({ overlay = false }) {
  const { user } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!overlay) return undefined;
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [overlay]);

  useEffect(() => {
    setMobileOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleSignOut = () => {
    signOut();
    navigate('/');
  };

  const headerClass = [
    'lx-header',
    overlay ? 'lx-header--overlay' : 'lx-header--solid',
    overlay && (scrolled || mobileOpen) && 'lx-header--scrolled',
  ].filter(Boolean).join(' ');

  return (
    <>
      <header className={headerClass}>
        <div className="lx-container lx-header-inner">
          <Link to="/" className="lx-wordmark" aria-label="RoolDrive home">
            Rool<span>Drive</span>
          </Link>

          <nav className="lx-nav" aria-label="Main">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className="lx-nav-link">
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="lx-header-actions">
            {user ? (
              <div className="lx-account lx-hide-mobile" ref={menuRef}>
                <button
                  type="button"
                  className="lx-account-btn"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((open) => !open)}
                >
                  <span className="lx-avatar" aria-hidden>{initials(user)}</span>
                  {firstName(user) || 'Account'}
                </button>
                {menuOpen && (
                  <div className="lx-menu" role="menu">
                    <Link to="/dashboard/bookings" className="lx-menu-item" role="menuitem">
                      <PiCalendarCheck /> My rides
                    </Link>
                    <Link to="/dashboard/profile" className="lx-menu-item" role="menuitem">
                      <PiUserCircle /> Profile
                    </Link>
                    <button type="button" className="lx-menu-item" role="menuitem" onClick={handleSignOut}>
                      <PiSignOut /> Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button variant="ghost" size="sm" className="lx-hide-mobile" onClick={() => setAuthOpen(true)}>
                Sign in
              </Button>
            )}
            <Button to="/book" size="sm" className="lx-hide-mobile">Book now</Button>
            <button
              type="button"
              className="lx-round-btn lx-menu-toggle"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((open) => !open)}
            >
              {mobileOpen ? <PiX aria-hidden /> : <PiList aria-hidden />}
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <nav className="lx-mobile-nav" aria-label="Main">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className="lx-nav-link">
              {item.label}
            </NavLink>
          ))}
          {user && (
            <>
              <NavLink to="/dashboard/bookings" className="lx-nav-link">My rides</NavLink>
              <NavLink to="/dashboard/profile" className="lx-nav-link">Profile</NavLink>
            </>
          )}
          <div className="lx-mobile-nav-actions">
            <Button to="/book" size="lg" block>Book now</Button>
            {user ? (
              <Button variant="secondary" size="lg" block onClick={handleSignOut}>Sign out</Button>
            ) : (
              <Button variant="secondary" size="lg" block onClick={() => { setMobileOpen(false); setAuthOpen(true); }}>
                Sign in
              </Button>
            )}
          </div>
        </nav>
      )}

      {authOpen && (
        <AuthModal
          isOpen={authOpen}
          onClose={() => setAuthOpen(false)}
          onSuccess={() => { notifySessionChange(); setAuthOpen(false); }}
        />
      )}
    </>
  );
}
